/**
 * useOpsNav — single source of truth for the ops sidebar navigation.
 *
 * Extracted from OpsLayout.vue so that both the sidebar and the ⌘K command
 * palette consume the exact same role-aware nav (icons + role gating included).
 * Adding a nav item here surfaces it in both places automatically.
 */
import { computed, reactive, type ComputedRef } from 'vue'
import { useAuthStore } from '../stores/auth'

// Nav items support one level of nesting (a parent with `children` and no route).
export interface NavItem {
  label: string
  icon: string
  route?: string
  children?: NavItem[]
  /**
   * A count rendered beside the label. `undefined` (the default for every item
   * that never calls setNavBadge) renders nothing at all, so this field is a
   * no-op for the whole existing nav.
   *
   * `null` is meaningful and distinct from `0`: null is "not counted yet", 0 is
   * "counted, and it is zero". Neither renders a pill — a badge is a call to
   * action and both of those are "nothing to do" — but the store that owns the
   * count can tell them apart, which is the same not_loaded-vs-empty
   * distinction the legal screens are built around.
   */
  badge?: number | null
  /**
   * Select this item ONLY on its own path, never on a descendant of it.
   *
   * The default (prefix matching) is what a sidebar wants almost everywhere:
   * `Vehicle Search` at `/atlas/vehicles` should stay lit on
   * `/atlas/vehicles/1FUJ...`, and `Watchlists` at `/atlas/lists` on a list
   * detail. It is wrong for exactly one shape — a leaf whose path is also the
   * PARENT of its siblings' paths.
   *
   * `Open by DOT` is that leaf (AST3-2, GPT-6 Astra, 2026-09-22). It was
   * already `/atlas`; wave 4 moved the rest of the group underneath it, and a
   * prefix match then lit it on `/atlas/search`, `/atlas/lists`,
   * `/atlas/portfolio`, `/atlas/vehicles` and both reports screens — two
   * sidebar rows selected at once on every sibling Atlas screen. Nothing in the
   * repath was wrong; the predicate was, and it had been right for three years
   * because no nav item had ever been an ancestor of another.
   *
   * Set it on a leaf whose route is a strict prefix of another item's route.
   * Leave it off otherwise, or descendant screens lose their section highlight.
   */
  exact?: boolean
}

/**
 * Is `item` the nav row that should be lit while the app is at `currentPath`?
 *
 * Exported and pure so it can be asserted directly. The predicate used to live
 * inline in `OpsLayout.vue`'s setup, which is why AST3-2's repro had to pull it
 * out with a regex over the SFC — a nav rule with no reachable oracle. It reads
 * the same `NavItem` the sidebar renders, so a test drives the real data.
 *
 * Prefix by default (a descendant screen keeps its section lit), exact when the
 * item asks for it. `NavItem.exact` documents when to ask.
 */
export function isNavItemActive(
  currentPath: string,
  item: Pick<NavItem, 'route' | 'exact'>,
): boolean {
  if (!item.route) return false
  if (item.route === '/' || item.exact) return currentPath === item.route
  // Whole path segments only: `/sales/conversion` is not an ancestor of
  // `/sales/conversion-review`, nor `/admin/crm` of `/admin/crm-rule-activity`.
  return currentPath === item.route || currentPath.startsWith(item.route + '/')
}

/**
 * The ONE route the sidebar lights at `currentPath`: the most specific item
 * that matches, or null when no item owns the screen.
 *
 * `isNavItemActive` alone lit every ancestor too. With overview rows in the
 * same lists as their screens (`Admin Home` at `/admin`, `Overview` at
 * `/transportation`, `Dashboard` at `/compliance` and `/admin/bi`), an admin
 * saw two or three rows selected on 46 screens (ops audit 2026-10-02, shell
 * #10). Picking the longest matching route keeps a descendant screen's section
 * lit (`/atlas/lists/123` → Watchlists) and drops the ancestor beside it.
 * `NavItem.exact` still means "never on a descendant".
 */
export function activeNavRoute(currentPath: string, items: NavItem[]): string | null {
  let best: string | null = null
  const visit = (list: NavItem[]) => {
    for (const item of list) {
      if (item.children) visit(item.children)
      if (item.route && isNavItemActive(currentPath, item) && (!best || item.route.length > best.length)) {
        best = item.route
      }
    }
  }
  visit(items)
  return best
}

/**
 * Nav badge registry.
 *
 * ONE read feeds both the badge and the screen (SH-4/SH-5, S22-C8). The store
 * that fetches a worklist calls `setNavBadge(route, total)` with the SAME total
 * it renders; the nav never issues a count of its own. Two reads can disagree
 * about how many things are waiting; one cannot.
 *
 * Keyed by route path because that is what a NavItem already carries and what
 * the caller already knows — no id to keep in step.
 */
const navBadges = reactive<Record<string, number | null>>({})

/** Publish a count for `route`. `null` clears it back to not-counted. */
export function setNavBadge(route: string, count: number | null): void {
  navBadges[route] = count
}

/** Read a published count. Exported for tests and for the ⌘K palette. */
export function getNavBadge(route: string): number | null | undefined {
  return navBadges[route]
}

/** Drop every published count. Called on logout alongside the store resets. */
export function clearNavBadges(): void {
  for (const key of Object.keys(navBadges)) delete navBadges[key]
}

/**
 * The Legal department nav (GW-1, partial by design).
 *
 * GW-1 asks for a layered surface over six departments; one department exists,
 * so this section ADMITS six and populates one. A second department is a second
 * section beside this one, not a rewrite of it.
 *
 * Every route here is declared in ops/src/router/legal.ts under a parent
 * carrying `meta.roles = LEGAL_ROUTE_ROLES`, so an entry that appears in this
 * list is an entry the reader will actually be admitted to — the "nav item
 * pointing at a route that bounces you" failure the developer role already
 * recorded in this file.
 */
const LEGAL_ITEMS: NavItem[] = [
  { label: 'Waiting on you', icon: 'pi pi-inbox', route: '/legal/waiting' },
  { label: 'Matters', icon: 'pi pi-briefcase', route: '/legal/matters' },
  { label: 'Population Grids', icon: 'pi pi-table', route: '/legal/grids' },
  { label: 'Jobs', icon: 'pi pi-play', route: '/legal/jobs' },
  { label: 'Spend', icon: 'pi pi-dollar', route: '/legal/spend' },
  { label: 'Audit', icon: 'pi pi-shield', route: '/legal/audit' },
]

/** The Legal section, with live badge counts applied. */
function legalSection(): NavSection {
  return {
    title: 'Legal',
    items: LEGAL_ITEMS.map((item) => ({ ...item, badge: navBadges[item.route!] })),
  }
}

export interface NavSection {
  title: string
  items: NavItem[]
}

// Hiring-team allowlist — mirrors the backend HIRING_TEAM_EMAILS so the Hiring
// nav entry shows for the hiring team (Dugger + Kathy) without making Kathy an
// admin. UX gate only; the API is enforced by CurrentHiringTeam.
const HIRING_TEAM_EMAILS = ['zach.dugger@claimshieldpro.com', 'kathy@claimshieldpro.com']

// Atlas — ClaimShield's carrier-intelligence product, and the CAB replacement.
// One list, shared by all three nav variants, because every earlier attempt to
// keep two copies "in sync" by comment has drifted (see the Call Queue and
// Opportunities notes below, both of which say exactly that).
//
// Ahmed ruled on 2026-09-14 that Atlas names the product surface and not just
// the repo directory, so everything CAB-replacement lives under this one group
// rather than scattered across Sales. Spec: Nauman's tier-B B10
// (knowledge/atlas/plans/2026-09-11-tier-b-prod-proven-plan.md), approved 2026-09-14.
// Naming and the Remedy boundary: knowledge/atlas/naming.md.
//
// Every entry here resolves to a screen that works when clicked. The three
// tools whose routes need a parameter — BASICs Calculator (/atlas/carriers/
// :dot/calculator), Parity Audit (/atlas/carriers/:dot/parity) and Shipper
// Search (/atlas/shippers/:name) — are reached through "Open by DOT" rather
// than listed directly, because a nav entry pointing at a route that cannot be
// resolved without typing a URL is the dead end this group exists to remove.
// (Wave 6.1: this file emits no templated carrier path. Its entries are
// static `route:` strings, not pushes, because `isNavItemActive` prefix-matches
// them against the current path, so they stay paths on the §3.5 family.)
//
// PATHS: the §3.5 route family (Atlas_Architecture.md, F1 RULED — adopt
// `/atlas/*` now, the old paths redirect permanently, route NAMES stay stable).
// Eight of the nine moved here in wave 4. Two notes a reader will want:
//
//   · `Saved Carriers` deliberately keeps `/saved-carriers`. §3.5 folds it into
//     `/atlas/lists` ("Watchlists (Saved Carriers = default)"), but that fold IS
//     F5 — retiring the browser-local `useSavedCarriers.ts` behind a one-time
//     migration — and F5 is wave 5. Repointing the label now would hand two nav
//     entries one destination and strand every star still sitting in a
//     consultant's localStorage. It collapses into `/atlas/lists` when F5 lands.
//   · `Open by DOT` was already `/atlas` and stays there. `/atlas/status` is a
//     different screen and is not in this group. It carries `exact: true`
//     because wave 4 made its path the PARENT of six siblings in this same
//     list, and the sidebar's prefix match then lit two rows at once on every
//     one of them (AST3-2).
const PROSPECT_SEARCH_ITEM: NavItem = {
  label: 'Prospect Search',
  icon: 'pi pi-search-plus',
  route: '/prospects',
}

// Bulk and Scheduled Reports are staff + admin only since the Atlas wave-2
// carve ruled the atlas_reports router to Staff (2026-09-21), so the developer
// copy below withholds them like Prospect Search.
const BULK_REPORTS_ITEM: NavItem = {
  label: 'Bulk Reports',
  icon: 'pi pi-file-export',
  route: '/atlas/bulk-reports',
}
const SCHEDULED_REPORTS_ITEM: NavItem = {
  label: 'Scheduled Reports',
  icon: 'pi pi-calendar',
  route: '/atlas/scheduled-reports',
}

const ATLAS_ITEMS: NavItem[] = [
  { label: 'Carrier Search', icon: 'pi pi-search', route: '/atlas/search' },
  { label: 'Open by DOT', icon: 'pi pi-directions', route: '/atlas', exact: true },
  { label: 'Saved Carriers', icon: 'pi pi-star', route: '/saved-carriers' },
  { label: 'Watchlists', icon: 'pi pi-eye', route: '/atlas/lists' },
  { label: 'Portfolio', icon: 'pi pi-chart-pie', route: '/atlas/portfolio' },
  { label: 'Vehicle Search', icon: 'pi pi-truck', route: '/atlas/vehicles' },
  PROSPECT_SEARCH_ITEM,
  BULK_REPORTS_ITEM,
  SCHEDULED_REPORTS_ITEM,
]

/**
 * ATLAS_ITEMS minus the entries the `developer` role is withheld from: Prospect
 * Search, and the two report screens (their routes and API admit staff + admin
 * only, so a developer link to them landed on the access-denied page; ops audit
 * 2026-10-02).
 *
 * WHY IDENTITY AND NOT A PATH. This used to be
 * `ATLAS_ITEMS.filter((i) => i.route !== '/insurance/robley-prospect-search')`
 * — a string equality on a path that wave 4 changes. The moment the item became
 * `/prospects` the filter stopped matching, and a filter that stops matching
 * does not throw: it silently returns everything, putting Prospect Search into
 * the developer sidebar. `developer` is withheld from `/prospects` by D-15, so
 * that is a role grant made by a typo. Comparing the OBJECT cannot drift with
 * the path, and it will not drift again in wave 5 or wave 6 either.
 */
const WITHHELD_FROM_DEVELOPER: ReadonlySet<NavItem> = new Set([
  PROSPECT_SEARCH_ITEM,
  BULK_REPORTS_ITEM,
  SCHEDULED_REPORTS_ITEM,
])
const ATLAS_ITEMS_FOR_DEVELOPER: NavItem[] = ATLAS_ITEMS.filter(
  (item) => !WITHHELD_FROM_DEVELOPER.has(item),
)

export function useOpsNav(): { navSections: ComputedRef<NavSection[]> } {
  const authStore = useAuthStore()

  const isHiringTeam = computed(() => {
    if (authStore.isAdmin) return true
    const email = (authStore.user?.email || '').toLowerCase()
    return HIRING_TEAM_EMAILS.includes(email)
  })

  const navSections = computed((): NavSection[] => {
    const isAdmin = authStore.isAdmin

    // Developer — restricted role: the surfaces a Genfactor engineer BUILDS,
    // and nothing else. Checked before the staff branch so a developer
    // (isAdmin=false) doesn't fall through into the staff nav and see
    // Clients/Drivers/financials.
    //
    // This list is the THIRD layer of the same grant and it is the one that is
    // easy to forget: the API dependency (require_roles) and the router's
    // meta.roles were both widened on 2026-08-21, and without this a developer
    // could reach none of it — every new surface was open and unreachable,
    // navigable only by typing a URL. Widening a role means all three or the
    // grant is theatre. Keep this in step with the approved list, which is
    // pinned in tests/auth/test_developer_surface_grants.py.
    if (authStore.isDeveloper) {
      return [
        {
          title: 'Review',
          items: [{ label: 'AI Review Queue', icon: 'pi pi-verified', route: '/file-review' }],
        },
        {
          title: 'Compliance',
          items: [
            { label: 'Safety Scores', icon: 'pi pi-chart-line', route: '/compliance/safety-scores' },
            { label: 'DataQ', icon: 'pi pi-flag', route: '/compliance/dataq' },
          ],
        },
        {
          title: 'Drivers',
          items: [
            { label: 'Driver Files', icon: 'pi pi-folder', route: '/transportation/driver-files' },
          ],
        },
        {
          title: 'Documents',
          items: [{ label: 'E-Sign', icon: 'pi pi-pencil', route: '/esign' }],
        },
        // Course Runs — the operator console for the agentic course pipeline.
        // list_runs / get_run are CurrentStaffOrDeveloper (2026-09-26); the
        // start/pause/resume/cancel controls inside the view are gated by the
        // /access can_operate flag, which a developer may or may not hold.
        {
          title: 'Training',
          items: [{ label: 'Course Runs', icon: 'pi pi-video', route: '/course-runs' }],
        },
        // Migration Waves — only for the developers named in Sentinel's
        // MIGRATION_LEADS (Ahmed, 2026-10-28), read from the /users/me hint.
        // Every other developer would be refused by every call the page makes,
        // so the entry is not built for them at all. The hint is not the
        // control: CurrentMigrationLead is, on each /migration-waves route.
        ...(authStore.user?.migration_lead
          ? [
              {
                title: 'Migration',
                items: [{ label: 'Migration Waves', icon: 'pi pi-sitemap', route: '/migration-waves' }],
              },
            ]
          : []),
        // Atlas — full, as of 2026-09-14. Ahmed made Nauman co-lead on Atlas
        // and ruled he gets the whole front end rather than a per-endpoint
        // grant, because he built tier A and could not open a single screen to
        // verify his own work.
        //
        // This supersedes the 2026-09-14 00:36 version of this group, which was
        // titled "Carriers", held Saved Carriers alone, and withheld Carrier
        // Search — so it opened to an empty bookmark list with no way to reach
        // a carrier. Carrier Search is now open at the three endpoints it
        // actually needs (fmcsa.py carrier_search / state_counts / export),
        // not by opening that 5,000-line router wholesale.
        //
        // Why this hands over no client book: the profile, calculator, parity
        // and search paths are carrier-level FMCSA data keyed by DOT and touch
        // no Client or Employee row; watchlists are carrier-level too;
        // scheduled/bulk reports filter on `user_id == user.id`; and Portfolio
        // — the one surface that does read Clients — was scoped in the same
        // change (fmcsa_portfolio.py::_book_dots), so a developer sees the Test
        // clients `client_scope` grants that role, never the live book.
        //
        // ⚠ Prospect Search is the one ATLAS_ITEMS entry withheld here. It is
        // served by NurtureActor (robley_nurture.py), which also guards Robley
        // campaign CRUD, the outreach cockpit and the nurture board — widening
        // it would open David's insurance system, which is a separate decision
        // from Atlas. Staff and admin keep it; open it for developer only if
        // that Robley question is answered on its own terms.
        {
          title: 'Atlas',
          items: ATLAS_ITEMS_FOR_DEVELOPER,
        },
        {
          title: 'Account',
          items: [{ label: 'Profile', icon: 'pi pi-user', route: '/profile' }],
        },
      ]
    }

    // Legal — restricted role: the legal department's own surfaces and nothing
    // else. Checked BEFORE the staff branch for the same reason the developer
    // branch above is: `legal` has is_staff=false and is_admin=false, so
    // `isAdmin` is false for it and without this branch it falls straight
    // through into the staff nav and is shown ~46 entries, every one of which
    // bounces it at the guard (SH-3, SH-11). A nav full of doors that refuse
    // you is worse than no nav.
    //
    // I7's "absent, not merely hidden": this section is BUILT for legal and
    // admin and never constructed for anyone else, so it is absent from the
    // DOM rather than v-show'd away. The enforcement is still the route
    // guard's meta.roles and the API's require_roles — a nav is not a
    // permission — but all three layers have to agree or the grant is theatre.
    if (authStore.isLegal) {
      return [
        legalSection(),
        {
          title: 'Account',
          items: [{ label: 'Profile', icon: 'pi pi-user', route: '/profile' }],
        },
      ]
    }

    // Staff gets the same grouped sidebar as admin, scoped to staff-accessible
    // items. (Was a flat list until 2026-07-14 — at ~25 items flat stopped
    // scanning well, and the grouped section cards from sketch 056-C exist
    // precisely to solve that. Item order preserved within each domain.)
    if (!isAdmin) {
      const toolsItems: NavItem[] = [
        { label: 'Document Signing', icon: 'pi pi-pencil', route: '/esign' },
        { label: 'Application Dashboard', icon: 'pi pi-file-edit', route: '/bi/applications' },
        { label: 'Guides', icon: 'pi pi-question-circle', route: '/guides' },
        { label: 'Requests', icon: 'pi pi-megaphone', route: '/support/requests' },
        // Client-support Phase 2C — carriers waiting on a reply in the portal
        // thread. Next to Requests because both are "someone is waiting on us".
        { label: 'Client Messages', icon: 'pi pi-comments', route: '/support/messages' },
        { label: 'Reports', icon: 'pi pi-chart-bar', route: '/reports' },
        // What the driver-record automation did, and why it stayed quiet. This
        // is the consultant's answer to "my status change did nothing" —
        // runbook §3a triages that report from this screen. Staff by Ahmed's
        // ruling 2026-09-11; the route's own meta.roles carries the override.
        { label: 'CRM Rule Activity', icon: 'pi pi-sync', route: '/admin/crm-rule-activity' },
        { label: 'Notification Preferences', icon: 'pi pi-bell', route: '/notification-preferences' },
        { label: 'Profile', icon: 'pi pi-user', route: '/profile' },
      ]
      // Hiring (HR pipeline) — only for hiring-team staff (e.g. Kathy).
      if (isHiringTeam.value) {
        toolsItems.splice(toolsItems.length - 2, 0, {
          label: 'Hiring',
          icon: 'pi pi-id-card',
          route: '/admin/careers/applications',
        })
      }
      return [
        {
          title: 'Overview',
          items: [
            { label: 'Dashboard', icon: 'pi pi-home', route: '/' },
            // Staff since 2026-10-01 (Dugger). Sits with Dashboard because the
            // Book is the same data as the dashboard, gathered per consultant.
            { label: 'Book', icon: 'pi pi-book', route: '/book' },
            ...(import.meta.env.VITE_PORTAL_ACCESS_REVIEW_ENABLED === '1'
              ? [{ label: 'Portal Requests', icon: 'pi pi-key', route: '/portal-access-requests' }]
              : []),
            { label: 'Action Center Coverage', icon: 'pi pi-eye', route: '/action-center-coverage' },
            { label: 'Audit Readiness', icon: 'pi pi-verified', route: '/audit-readiness/worst' },
          ],
        },
        {
          title: 'Transportation',
          items: [
            { label: 'Clients', icon: 'pi pi-building', route: '/transportation/clients' },
            // Next to Clients because it is a client-level worklist: carriers
            // whose drivers see plain text instead of the company's logo.
            { label: 'Unbranded Carriers', icon: 'pi pi-image', route: '/transportation/unbranded-carriers' },
            { label: 'Drivers', icon: 'pi pi-users', route: '/transportation/drivers' },
            // Sits under Drivers because it is a driver-level worklist: active
            // drivers whose employment classification nobody has confirmed.
            { label: 'Needs Classification', icon: 'pi pi-question-circle', route: '/transportation/drivers-needing-classification' },
            { label: 'VOE', icon: 'pi pi-id-card', route: '/transportation/voe' },
            { label: 'Criminal Checks', icon: 'pi pi-shield', route: '/transportation/criminal-checks' },
            { label: 'Driver Files', icon: 'pi pi-folder-open', route: '/transportation/driver-files' },
            { label: 'Driver File Review', icon: 'pi pi-folder-open', route: '/transportation/driver-file-review' },
          ],
        },
        {
          title: 'Compliance',
          items: [
            { label: 'Inspections', icon: 'pi pi-clipboard', route: '/compliance/inspections' },
            // §396.9(d)(3) 15-day certification chase list — consultant-facing (F2)
            { label: 'Overdue Certifications', icon: 'pi pi-clock', route: '/compliance/overdue-certifications' },
            { label: 'Violations', icon: 'pi pi-exclamation-triangle', route: '/compliance/violations' },
            { label: 'Safety Scores', icon: 'pi pi-chart-line', route: '/compliance/safety-scores' },
            { label: 'DataQ Challenges', icon: 'pi pi-flag', route: '/compliance/dataq' },
            ...(authStore.user?.audit_desk ? [{ label: 'Audit Desk', icon: 'pi pi-clipboard', route: '/audit-desk' }] : []),
            { label: 'Repairs', icon: 'pi pi-wrench', route: '/fleet/repairs' },
            { label: 'Invoice Review', icon: 'pi pi-receipt', route: '/fleet/invoice-review' },
            ...(!authStore.isClient ? [
              { label: 'Roadside Review', icon: 'pi pi-link', route: '/fleet/roadside-review' },
              { label: 'Portal Reports', icon: 'pi pi-upload', route: '/fleet/portal-reports' },
            ] : []),
          ],
        },
        {
          title: 'Consortium',
          items: [{ label: 'Dashboard', icon: 'pi pi-verified', route: '/consortium' }],
        },
        {
          title: 'Training',
          items: [{ label: 'Training Library', icon: 'pi pi-book', route: '/training-library' }],
        },
        {
          title: 'Sales',
          items: [
            { label: 'Carrier Search', icon: 'pi pi-search', route: '/atlas/search' },
            // Opportunity board (crm-retirement slice 2). Placed right after
            // Carrier Search on purpose: ui-map principle 1 is "keep his nav
            // order" -- prospects, then opportunities, then clients. STAFF;
            // mirrored in the other branch, keep the two in sync.
            { label: 'Opportunities', icon: 'pi pi-th-large', route: '/sales/opportunities' },
            // Deal history (crm-retirement slice 1). Every attempt ever made,
            // searchable by name, DOT or phone -- the archive his board
            // deliberately hides. STAFF; mirrored in the admin branch below,
            // keep the two in sync.
            { label: 'History', icon: 'pi pi-history', route: '/sales/history' },
            // Vehicle Search and Portfolio moved to the Atlas group (2026-09-14).
            // Carrier Search stays here as well, and is the one deliberate
            // duplicate: the order above is a recorded rule for Prosser —
            // prospects, then opportunities, then clients — and Carrier Search
            // is the "prospects" step.
            { label: 'Proposals', icon: 'pi pi-file-edit', route: '/sales/proposals' },
            { label: 'Conversion Cockpit', icon: 'pi pi-dollar', route: '/sales/conversion' },
            { label: 'Contact Review', icon: 'pi pi-user-edit', route: '/sales/conversion-review' },
            // Broker drift leads — carriers in a broker's network heading
            // toward trouble. Sales, not Compliance: the output is a call.
            { label: 'Drift Leads', icon: 'pi pi-chart-line', route: '/sales/drift-leads' },
            { label: 'Carrier Disputes', icon: 'pi pi-flag', route: '/sales/carrier-disputes' },
            // Prospect call queue (consultant-workbench P3.3). THIS is the
            // entry the cockpit was built for: Prosser is `staff`, so until
            // 2026-09-04 the one screen made for him was admin-only and he
            // could not reach it. Ahmed ruled `staff` on 2026-08-28. Mirrored
            // in the admin branch below; keep the two in sync.
            { label: 'Call Queue', icon: 'pi pi-phone', route: '/sales/call-queue' },
            { label: 'Insurance Submissions', icon: 'pi pi-shield', route: '/insurance/submissions' },
            { label: 'Loss Run Review', icon: 'pi pi-receipt', route: '/loss-run-extractions' },
            { label: 'Engine Pull List', icon: 'pi pi-list-check', route: '/insurance/engine-pull-list' },
          ],
        },
        { title: 'Atlas', items: ATLAS_ITEMS },
        { title: 'Tools', items: toolsItems },
      ]
    }

    // Admin gets full structured nav with section headers
    const sections: NavSection[] = [
      {
        title: 'Overview',
        items: [
          { label: 'Dashboard', icon: 'pi pi-home', route: '/' },
          { label: 'Action Center', icon: 'pi pi-inbox', route: '/action-center' },
          { label: 'Action Center Coverage', icon: 'pi pi-eye', route: '/action-center-coverage' },
          { label: 'Audit Readiness', icon: 'pi pi-verified', route: '/audit-readiness/worst' },
          { label: 'Book', icon: 'pi pi-book', route: '/book' },
          ...(import.meta.env.VITE_PORTAL_ACCESS_REVIEW_ENABLED === '1'
            ? [{ label: 'Portal Requests', icon: 'pi pi-key', route: '/portal-access-requests' }]
            : []),
        ],
      },
      {
        title: 'Transportation',
        items: [
          { label: 'Overview', icon: 'pi pi-truck', route: '/transportation' },
          { label: 'Clients', icon: 'pi pi-building', route: '/transportation/clients' },
          { label: 'Drivers', icon: 'pi pi-users', route: '/transportation/drivers' },
          { label: 'Needs Classification', icon: 'pi pi-question-circle', route: '/transportation/drivers-needing-classification' },
          { label: 'Unbranded Carriers', icon: 'pi pi-image', route: '/transportation/unbranded-carriers' },
          { label: 'MVRs', icon: 'pi pi-file-pdf', route: '/transportation/mvrs' },
          { label: 'PSPs', icon: 'pi pi-file-check', route: '/transportation/psps' },
          { label: 'Criminal Checks', icon: 'pi pi-shield', route: '/transportation/criminal-checks' },
          { label: 'VOE', icon: 'pi pi-id-card', route: '/transportation/voe' },
          { label: 'Driver Files', icon: 'pi pi-folder-open', route: '/transportation/driver-files' },
          { label: 'Driver File Review', icon: 'pi pi-folder-open', route: '/transportation/driver-file-review' },
          { label: 'AI Review Queue', icon: 'pi pi-verified', route: '/file-review' },
        ],
      },
      {
        title: 'Compliance',
        items: [
          { label: 'Dashboard', icon: 'pi pi-shield', route: '/compliance' },
          { label: 'Inspections', icon: 'pi pi-clipboard', route: '/compliance/inspections' },
          // §396.9(d)(3) 15-day certification chase list — consultant-facing (F2)
          { label: 'Overdue Certifications', icon: 'pi pi-clock', route: '/compliance/overdue-certifications' },
          { label: 'Vehicles', icon: 'pi pi-car', route: '/compliance/vehicles' },
          { label: 'Violations', icon: 'pi pi-exclamation-triangle', route: '/compliance/violations' },
          { label: 'Safety Scores', icon: 'pi pi-chart-line', route: '/compliance/safety-scores' },
          { label: 'DataQ Challenges', icon: 'pi pi-flag', route: '/compliance/dataq' },
          ...(authStore.user?.audit_desk ? [{ label: 'Audit Desk', icon: 'pi pi-clipboard', route: '/audit-desk' }] : []),
          { label: 'Repairs', icon: 'pi pi-wrench', route: '/fleet/repairs' },
          { label: 'Invoice Review', icon: 'pi pi-receipt', route: '/fleet/invoice-review' },
          { label: 'Roadside Review', icon: 'pi pi-link', route: '/fleet/roadside-review' },
          { label: 'Portal Reports', icon: 'pi pi-upload', route: '/fleet/portal-reports' },
        ],
      },
      {
        title: 'Consortium',
        items: [
          { label: 'Dashboard', icon: 'pi pi-verified', route: '/consortium' },
        ],
      },
      {
        title: 'Remedial Training',
        items: [
          { label: 'Dashboard', icon: 'pi pi-book', route: '/remedial-training' },
          { label: 'Assignments', icon: 'pi pi-list-check', route: '/remedial-training/assignments' },
          { label: 'Course Catalog', icon: 'pi pi-graduation-cap', route: '/remedial-training/courses' },
          { label: 'Unmatched Violations', icon: 'pi pi-question-circle', route: '/remedial-training/unmatched-violations' },
        ],
      },
      {
        title: 'Training',
        items: [
          { label: 'Training Library', icon: 'pi pi-book', route: '/training-library' },
          { label: 'Training Review', icon: 'pi pi-verified', route: '/training-review' },
          { label: 'Course Runs', icon: 'pi pi-video', route: '/course-runs' },
        ],
      },
      {
        title: 'Industrial',
        items: [{ label: 'Overview', icon: 'pi pi-briefcase', route: '/industrial' }],
      },
      {
        title: 'Company',
        items: [
          {
            label: 'Presentations',
            icon: 'pi pi-megaphone',
            children: [
              { label: 'ClaimShield 2.026', icon: 'pi pi-play', route: '/presentations/q1-2026-kickoff' },
            ],
          },
        ],
      },
      {
        title: 'Sales',
        items: [
          { label: 'Carrier Search', icon: 'pi pi-search', route: '/atlas/search' },
          // Opportunity board (crm-retirement slice 2). Placed right after
          // Carrier Search on purpose: ui-map principle 1 is "keep his nav
          // order" -- prospects, then opportunities, then clients. STAFF;
          // mirrored in the other branch, keep the two in sync.
          { label: 'Opportunities', icon: 'pi pi-th-large', route: '/sales/opportunities' },
          // Deal history (crm-retirement slice 1). Also in the staff branch
          // above; keep the two entries in sync.
          { label: 'History', icon: 'pi pi-history', route: '/sales/history' },
          // Vehicle Search and Portfolio moved to the Atlas group (2026-09-14);
          // Carrier Search stays, per the recorded nav order above.
          { label: 'Proposals', icon: 'pi pi-file-edit', route: '/sales/proposals' },
          { label: 'Conversion Cockpit', icon: 'pi pi-dollar', route: '/sales/conversion' },
          { label: 'Contact Review', icon: 'pi pi-user-edit', route: '/sales/conversion-review' },
          { label: 'Drift Leads', icon: 'pi pi-chart-line', route: '/sales/drift-leads' },
          { label: 'Carrier Disputes', icon: 'pi pi-flag', route: '/sales/carrier-disputes' },
          // Prospect call queue (consultant-workbench P3.3). Also in the staff
          // branch above as of 2026-09-04 — Ahmed ruled 2026-08-28 that the
          // sales cockpit runs on `staff`, and the 8 user-facing
          // /v1/prospect-queue endpoints it consumes are CurrentStaff to
          // match. Keep the two entries in sync.
          { label: 'Call Queue', icon: 'pi pi-phone', route: '/sales/call-queue' },
          {
            label: 'Insurance',
            icon: 'pi pi-shield',
            children: [
              { label: 'Robley Tasks', icon: 'pi pi-check-square', route: '/robley/tasks' },
              { label: 'Outreach Cockpit', icon: 'pi pi-phone', route: '/robley/outreach' },
              { label: 'Nurture Board', icon: 'pi pi-th-large', route: '/robley/nurture-board' },
              { label: 'Prospect Search', icon: 'pi pi-search-plus', route: '/prospects' },
              // One entry: the cockpit is per submission (`/insurance/submissions/:token/cockpit`),
              // opened from a row, so a second menu item to the same list promised a screen
              // that is not there (ops audit 2026-10-02, shell #16).
              { label: 'Submissions', icon: 'pi pi-file', route: '/insurance/submissions' },
              { label: 'Loss Run Review', icon: 'pi pi-receipt', route: '/loss-run-extractions' },
              { label: 'Carriers', icon: 'pi pi-address-book', route: '/insurance/carriers' },
              { label: 'Engine Pull List', icon: 'pi pi-list-check', route: '/insurance/engine-pull-list' },
            ],
          },
        ],
      },
      // Atlas — the carrier-intelligence product. Same list the staff branch
      // and the developer branch render; see ATLAS_ITEMS at the top of the file
      // for why it is one const rather than three copies.
      { title: 'Atlas', items: ATLAS_ITEMS },
      {
        title: 'Tools',
        items: [
          { label: 'Application Dashboard', icon: 'pi pi-file-edit', route: '/bi/applications' },
          { label: 'Document Signing', icon: 'pi pi-pencil', route: '/esign' },
          { label: 'Requests', icon: 'pi pi-megaphone', route: '/support/requests' },
          // Client-support Phase 2C — carriers waiting on a reply in the portal
          // thread. Next to Requests because both are "someone is waiting on us".
          { label: 'Client Messages', icon: 'pi pi-comments', route: '/support/messages' },
          { label: 'Guides', icon: 'pi pi-question-circle', route: '/guides' },
          { label: 'Reports', icon: 'pi pi-chart-bar', route: '/reports' },
          { label: 'Notification Preferences', icon: 'pi pi-bell', route: '/notification-preferences' },
          { label: 'Profile', icon: 'pi pi-user', route: '/profile' },
        ],
      },
    ]

    // Legal — admin sees it too, because the backend's CurrentLegal is
    // `require_roles(ADMIN, LEGAL)`: the three operators are ADMIN and pass it.
    // Placed before the Admin section so Legal reads as a department rather
    // than as an administration screen. Staff do NOT get this section and are
    // refused at the guard if they type the URL.
    if (isAdmin) {
      sections.push(legalSection())
    }

    // Admin section only for admins
    if (authStore.isAdmin) {
      sections.push({
        title: 'Admin',
        items: [
          { label: 'Admin Home', icon: 'pi pi-cog', route: '/admin' },
          { label: 'Users', icon: 'pi pi-users', route: '/admin/users' },
          { label: 'Service Catalog', icon: 'pi pi-shopping-bag', route: '/admin/service-catalog' },
          { label: 'Features', icon: 'pi pi-flag', route: '/admin/features' },
          { label: 'Logs', icon: 'pi pi-file', route: '/admin/logs' },
          { label: 'Webhooks', icon: 'pi pi-bolt', route: '/admin/webhooks' },
          { label: 'Scheduled Jobs', icon: 'pi pi-clock', route: '/admin/scheduled-jobs' },
          // Same screen the staff nav carries under Tools — listed here too
          // because the admin nav is built as its own list and shares nothing
          // with the staff branch, so an entry added to one is absent from the
          // other. Next to Scheduled Jobs: both answer "did the automation run".
          { label: 'CRM Rule Activity', icon: 'pi pi-sync', route: '/admin/crm-rule-activity' },
          // The WorkDrive->S3 migration's waves (2026-10-28). Admin-only in this
          // branch: the route admits admin + the named developer leads, never staff.
          { label: 'Migration Waves', icon: 'pi pi-sitemap', route: '/migration-waves' },
          { label: 'Fleet Stickers', icon: 'pi pi-tag', route: '/admin/fleet/stickers' },
          { label: 'Compliance Screening', icon: 'pi pi-check-circle', route: '/admin/compliance-screening' },
          { label: 'Audit', icon: 'pi pi-shield', route: '/admin/audit' },
          { label: 'Financials', icon: 'pi pi-chart-line', route: '/admin/financials' },
          { label: 'DCC Economics', icon: 'pi pi-dollar', route: '/admin/financials/dcc-economics' },
          { label: 'Cost Truth', icon: 'pi pi-calculator', route: '/admin/financials/cost-truth' },
          { label: 'Invoice Email', icon: 'pi pi-envelope', route: '/admin/billing/invoice-email' },
          {
            label: 'Business Intelligence',
            icon: 'pi pi-chart-pie',
            children: [
              { label: 'Dashboard', icon: 'pi pi-chart-pie', route: '/admin/bi' },
              { label: 'Clearinghouse Mgmt', icon: 'pi pi-verified', route: '/admin/bi/clearinghouse' },
              { label: 'Client Profitability', icon: 'pi pi-money-bill', route: '/admin/bi/profitability' },
              { label: 'Projects', icon: 'pi pi-list-check', route: '/admin/bi/projects' },
              { label: 'Goals', icon: 'pi pi-flag', route: '/admin/bi/goals' },
              { label: 'Monthly Recap', icon: 'pi pi-calendar', route: '/admin/bi/monthly-review' },
              { label: 'Financial Projection', icon: 'pi pi-chart-line', route: '/admin/bi/financial-projection' },
            ],
          },
          { label: 'DCC Management', icon: 'pi pi-dollar', route: '/admin/dcc' },
          { label: 'CRM Explorer', icon: 'pi pi-database', route: '/admin/crm' },
          { label: 'Careers — Postings', icon: 'pi pi-briefcase', route: '/admin/careers' },
          { label: 'Careers — Applications', icon: 'pi pi-id-card', route: '/admin/careers/applications' },
          { label: 'API Docs', icon: 'pi pi-code', route: '/admin/api-docs' },
          { label: 'Docs', icon: 'pi pi-book', route: '/admin/docs' },
        ],
      })
    }

    return sections
  })

  return { navSections }
}
